import React from "react";
import { Button } from "semantic-ui-react";
import { WidgetProps } from "../types";
import Prompt from "../Prompt";
import Summary, { SummaryField, SummaryPills, Actions } from "../Summary";

/**
 * "Your true hero" — strike out each hero's name to reveal your own. The point
 * is that the qualities you admire in others are already yours.
 *
 * This question's stored description is the placeholder "no question", so the
 * instruction lives here.
 */
const PROMPT =
  "Click each hero’s name to cross it out — and see who those qualities really belong to.";

interface Hero {
  name: string;
  qualities: string[];
}

const HeroesReveal: React.FC<WidgetProps> = ({
  payload,
  update,
  submit,
  submitting,
  finished,
  question,
  currentUser,
}) => {
  const heroes: Hero[] = payload.heroes || [];
  const revealed: string[] = payload.revealed || [];
  const learnerName = currentUser?.name || currentUser?.user?.name || "You";

  const reveal = (name: string) => {
    if (revealed.includes(name)) return;
    update({ ...payload, revealed: [...revealed, name] });
  };

  const allRevealed =
    heroes.length > 0 && heroes.every((h) => revealed.includes(h.name));

  if (finished) {
    return (
      <Summary question={question} currentUser={currentUser} prompt={PROMPT}>
        {heroes.map((hero) => (
          <SummaryField key={hero.name} label={learnerName}>
            <SummaryPills values={hero.qualities || []} />
          </SummaryField>
        ))}
      </Summary>
    );
  }

  return (
    <div className="ui form assignment-form">
      <Prompt question={question} currentUser={currentUser} fallback={PROMPT} />

      {heroes.map((hero) => {
        const isRevealed = revealed.includes(hero.name);
        return (
          <div key={hero.name} className="ix-section">
            <div className="ix-value-lg">
              {isRevealed ? (
                <>
                  <span className="line-through">{hero.name}</span>{" "}
                  <span className="ix-revealed">{learnerName}</span>
                </>
              ) : (
                <button
                  type="button"
                  className="dtlyl-hero-name-cross-out"
                  onClick={() => reveal(hero.name)}
                >
                  {hero.name}
                </button>
              )}
            </div>

            <div className="interactive-answer-pills">
              {(hero.qualities || [])
                .filter((q) => (q || "").trim())
                .map((q, i) => (
                  <span className="interactive-answer-pill" key={i}>
                    {q}
                  </span>
                ))}
            </div>
          </div>
        );
      })}

      <Actions>
        <Button
          primary
          circular
          className="challenge-submit"
          disabled={!allRevealed || submitting}
          loading={submitting}
          onClick={submit}
        >
          Finish
        </Button>
      </Actions>
    </div>
  );
};

export default HeroesReveal;
